<template>
  <!-- 包裹在 transition 中实现动画 -->
  <transition name="slide-up">
    <!-- 将 v-if 改为 v-show，这样元素始终存在于DOM中，只是显示/隐藏 -->
    <div
      v-show="isVisible"
      class="global-notification center-bottom"
      :class="type"
    >
      <div class="notification-content">
        <div class="notification-icon">
          <i :class="iconClass"></i>
        </div>
        <div class="notification-text" v-html="message"></div>
        <button
          class="notification-close"
          @click="closeNotification"
          aria-label="关闭通知"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "GlobalNotification",
  props: {
    type: {
      type: String,
      default: "info",
      validator: (value) => ["info", "warning", "success"].includes(value),
    },
    message: {
      type: String,
      default: "",
    },
    duration: {
      type: Number,
      default: 0,
    },
    storageKey: {
      type: String,
      default: "notification_closed",
    },
  },
  data() {
    return {
      // 初始设为false，避免闪烁
      isVisible: false,
    };
  },
  computed: {
    iconClass() {
      const icons = {
        info: "fas fa-info-circle",
        warning: "fas fa-exclamation-circle",
        success: "fas fa-check-circle",
      };
      return icons[this.type] || icons.info;
    },
  },
  created() {
    // 在组件创建时（渲染前）就检查本地存储
    this.checkVisibility();
  },
  mounted() {
    // 如果显示了，再设置自动关闭计时器
    if (this.isVisible && this.duration > 0) {
      setTimeout(() => {
        this.closeNotification();
      }, this.duration);
    }
  },
  methods: {
    checkVisibility() {
      // 检查本地存储，如果用户没有关闭过，就显示
      const isClosed = localStorage.getItem(this.storageKey) === "true";
      this.isVisible = !isClosed;
    },
    closeNotification() {
      this.isVisible = false;
      localStorage.setItem(this.storageKey, "true");
    },
  },
};
</script>

<style scoped>
/* 在Notification.vue的style中，替换或添加以下样式 */
.global-notification.center-bottom {
  /* 关键修改：水平居中，距离底部一定距离 */
  position: fixed;
  left: 50%;
  bottom: 30px;
  transform: translateX(-50%);
  right: auto; /* 覆盖原有的 right: 25px */
  max-width: 550px; /* 可以适当增加最大宽度 */
  width: 90%; /* 添加宽度百分比，适应小屏幕 */
  animation: float-center 3s ease-in-out infinite;
}

/* 调整漂浮动画，使其垂直浮动 */
@keyframes float-center {
  0%,
  100% {
    transform: translateX(-50%) translateY(0);
  }
  50% {
    transform: translateX(-50%) translateY(-8px);
  }
}

/* 响应式调整：在小屏幕上减少宽度 */
@media screen and (max-width: 768px) {
  .global-notification.center-bottom {
    width: 95%;
    max-width: none;
    bottom: 20px;
  }
}
/* 通知栏基础样式：固定在右下角，宽度自适应，有阴影和圆角 */
.global-notification {
  position: fixed;
  bottom: 25px;
  right: 25px;
  max-width: 450px;
  min-width: 300px;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  overflow: hidden;
  animation: float 3s ease-in-out infinite;
}
/* 当通知隐藏时，完全移除显示，避免占用空间 */
.global-notification:not([style*="display: none"]) {
  display: flex !important;
}
/* 根据类型设置背景色 */
.global-notification.info {
  background: linear-gradient(135deg, #6a5acd, #4a3cad);
  border-left: 5px solid var(--primary-color);
}
.global-notification.warning {
  background: linear-gradient(135deg, #ff9800, #f57c00);
  border-left: 5px solid #ff9800;
}
.global-notification.success {
  background: linear-gradient(135deg, #4caf50, #388e3c);
  border-left: 5px solid #4caf50;
}

.notification-content {
  display: flex;
  align-items: center;
  padding: 18px 20px;
  color: white;
}

.notification-icon {
  font-size: 1.8rem;
  margin-right: 18px;
  opacity: 0.9;
  flex-shrink: 0;
}

.notification-text {
  flex: 1;
  font-size: 0.95rem;
  line-height: 1.5;
}

/* 允许通知文本内的链接有特殊样式 */
.notification-text a {
  color: #ffeb3b;
  text-decoration: underline;
  font-weight: 600;
  transition: all 0.2s;
}
.notification-text a:hover {
  color: #fff;
  text-decoration: none;
}

.notification-close {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  margin-left: 15px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}
.notification-close:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

/* 从下方滑入的动画 */
.slide-up-enter-active {
  transition: all 0.4s cubic-bezier(0.68, -0.55, 0.27, 1.55);
}
.slide-up-leave-active {
  transition: all 0.3s ease;
}
.slide-up-enter-from {
  opacity: 0;
  transform: translateY(50px) scale(0.9);
}
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* 漂浮动画，增加活力感 */
@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

/* 响应式设计：在小屏幕上调整位置和宽度 */
@media screen and (max-width: 768px) {
  .global-notification {
    bottom: 20px;
    right: 15px;
    left: 15px;
    max-width: none;
    min-width: auto;
  }

  .notification-content {
    padding: 15px;
  }

  .notification-icon {
    font-size: 1.5rem;
    margin-right: 15px;
  }

  .notification-text {
    font-size: 0.9rem;
  }
}
</style>
